import type { LocalizedText } from "@/shared/i18n"

import type { CardStyle, CardStyleItemResponse } from "./card-style-types"

import arcticPreview from "@/assets/img/webp/card-style-arctic.webp"
import cavePreview from "@/assets/img/webp/card-style-cave.webp"
import forestPreview from "@/assets/img/webp/card-style-forest.webp"
import lunarPreview from "@/assets/img/webp/card-style-lunar.webp"
import moonlightPreview from "@/assets/img/webp/card-style-moonlight.webp"
import nightSkyPreview from "@/assets/img/webp/card-style-night-sky.webp"
import oceanPreview from "@/assets/img/webp/card-style-ocean.webp"
import pumpkinGlowPreview from "@/assets/img/webp/card-style-pumpkin-glow.webp"
import sakuraPreview from "@/assets/img/webp/card-style-sakura.webp"
import sunsetPreview from "@/assets/img/webp/card-style-sunset.webp"
import volcanoPreview from "@/assets/img/webp/card-style-volcano.webp"
import witchingHourPreview from "@/assets/img/webp/card-style-witching-hour.webp"

interface CardStyleVisual extends LocalizedText {
  gradient: string
  progressColor: string
  previewUrl?: string
}

const DEFAULT_VISUAL: CardStyleVisual = {
  key: "card-style-default",
  fallback: "Default",
  gradient: "from-slate-700 via-slate-600 to-slate-800",
  progressColor: "bg-sky-500"
}

const CARD_STYLE_VISUALS: Record<string, CardStyleVisual> = {
  cave: {
    key: "card-style-cave",
    fallback: "Cave",
    gradient: "from-[#24272D] via-[#17191E] to-[#0C0D11]",
    progressColor: "#14b8a6",
    previewUrl: cavePreview
  },
  sunset: {
    key: "card-style-sunset",
    fallback: "Sunset",
    gradient: "from-[#2A1262] via-[#6A2410] to-[#3A1406]",
    progressColor: "#ff7094",
    previewUrl: sunsetPreview
  },
  ocean: {
    key: "card-style-ocean",
    fallback: "Ocean",
    gradient: "from-[#0A3D80] via-[#072C60] to-[#031735]",
    progressColor: "#3a97ff",
    previewUrl: oceanPreview
  },
  forest: {
    key: "card-style-forest",
    fallback: "Forest",
    gradient: "from-[#08302A] via-[#051F18] to-[#020F0B]",
    progressColor: "#27b062",
    previewUrl: forestPreview
  },
  "night-sky": {
    key: "card-style-night-sky",
    fallback: "Night Sky",
    gradient: "from-[#42425F] via-[#383959] to-[#25263E]",
    progressColor: "#c0c8e0",
    previewUrl: nightSkyPreview
  },
  sakura: {
    key: "card-style-sakura",
    fallback: "Sakura",
    gradient: "from-[#44143A] via-[#300E2A] to-[#1A0818]",
    progressColor: "#f472b6",
    previewUrl: sakuraPreview
  },
  volcano: {
    key: "card-style-volcano",
    fallback: "Volcano",
    gradient: "from-[#121822] via-[#101216] to-[#240806]",
    progressColor: "#ff5a30",
    previewUrl: volcanoPreview
  },
  arctic: {
    key: "card-style-arctic",
    fallback: "Arctic",
    gradient: "from-[#0B3048] via-[#082436] to-[#041624]",
    progressColor: "#6cc3f0",
    previewUrl: arcticPreview
  },
  lunar: {
    key: "card-style-lunar",
    fallback: "Lunar",
    gradient: "from-[#1B1A3F] via-[#0F0F2A] to-[#0A0A1E]",
    progressColor: "#8b7cff",
    previewUrl: lunarPreview
  },
  gold: {
    key: "card-style-gold",
    fallback: "Gold",
    gradient: "from-yellow-600 via-amber-500 to-yellow-700",
    progressColor: "#fb923c"
  },
  emerald: {
    key: "card-style-emerald",
    fallback: "Emerald",
    gradient: "from-emerald-600 via-green-500 to-teal-600",
    progressColor: "#4ade80"
  },
  ruby: {
    key: "card-style-ruby",
    fallback: "Ruby",
    gradient: "from-rose-600 via-red-500 to-pink-600",
    progressColor: "#f87171"
  },
  neon: {
    key: "card-style-neon",
    fallback: "Neon",
    gradient: "from-purple-900 via-violet-800 to-purple-900",
    progressColor: "#a78bfa"
  },
  aurora: {
    key: "card-style-aurora",
    fallback: "Aurora",
    gradient: "from-teal-500 via-emerald-400 to-green-500",
    progressColor: "#facc15"
  },
  diamond: {
    key: "card-style-diamond",
    fallback: "Diamond",
    gradient: "from-slate-600 via-slate-500 to-slate-700",
    progressColor: "#c4b5fd"
  },
  inferno: {
    key: "card-style-inferno",
    fallback: "Inferno",
    gradient: "from-red-950 via-red-900 to-orange-950",
    progressColor: "#f97316"
  },
  eclipse: {
    key: "card-style-eclipse",
    fallback: "Eclipse",
    gradient: "from-slate-900 via-purple-950 to-slate-900",
    progressColor: "#fbbf24"
  },
  royal: {
    key: "card-style-royal",
    fallback: "Royal",
    gradient: "from-violet-900 via-purple-800 to-indigo-900",
    progressColor: "#fbbf24"
  },
  obsidian: {
    key: "card-style-obsidian",
    fallback: "Obsidian",
    gradient: "from-slate-950 via-slate-900 to-slate-950",
    progressColor: "#f59e0b"
  },
  "pumpkin-glow": {
    key: "card-style-pumpkin-glow",
    fallback: "Pumpkin Glow",
    gradient: "from-[#2A120A] via-[#140A07] to-[#0B0706]",
    progressColor: "#ff8a1f",
    previewUrl: pumpkinGlowPreview
  },
  moonlight: {
    key: "card-style-moonlight",
    fallback: "Moonlight",
    gradient: "from-[#1B1A3F] via-[#0F0F2A] to-[#0A0A1E]",
    progressColor: "#8b7cff",
    previewUrl: moonlightPreview
  },
  "witching-hour": {
    key: "card-style-witching-hour",
    fallback: "Witching Hour",
    gradient: "from-[#0F2A1C] via-[#0A1A12] to-[#06110B]",
    progressColor: "#7ce63a",
    previewUrl: witchingHourPreview
  },
  custom: {
    key: "card-style-custom",
    fallback: "Custom",
    gradient: "from-slate-900 via-slate-800 to-slate-950",
    progressColor: "#facc15"
  }
}

const normalizeCardStyleName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, "-")

export const mapCardStyleResponse = (style: CardStyleItemResponse): CardStyle => {
  const styleKey = normalizeCardStyleName(style.name)
  const visual = CARD_STYLE_VISUALS[styleKey] ?? {
    ...DEFAULT_VISUAL,
    key: `card-style-${styleKey}`,
    fallback: style.name
  }

  return {
    id: style.id,
    name: {
      key: visual.key,
      fallback: visual.fallback
    },
    gradient: visual.gradient,
    progressColor: visual.progressColor,
    category: style.category,
    previewUrl: style.preview_url ?? visual.previewUrl ?? null,
    requiredLevel: style.required_level > 0 ? style.required_level : undefined,
    priceXp: style.price_xp,
    sortOrder: style.sort_order
  }
}
